import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useBrand } from "../context/BrandContext";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "./ui/dialog";
import { Mail, Pencil, AlertCircle, CheckCircle2, Plus, Building2, Upload } from "lucide-react";
import { toast } from "sonner";

const isPlaceholder = (email) =>
  !email || email.endsWith("@salamarket.fr") || email.endsWith("@dpsmarket.fr");

const postalCode = (c) => {
  const m = `${c.address || ""} ${c.city || ""}`.match(/\b\d{5}\b/);
  return m ? m[0] : "";
};

function DepotFields({ form, set }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="col-span-2">
        <Label>Nom du dépôt</Label>
        <Input data-testid="cf-name" value={form.name || ""} onChange={(e) => set("name", e.target.value)} placeholder="Ex : Dépôt Lyon" />
      </div>
      <div className="col-span-2">
        <Label>Email du dépôt</Label>
        <Input data-testid="cf-email" type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} placeholder="depot@exemple.fr" />
      </div>
      <div>
        <Label>Contact</Label>
        <Input data-testid="cf-contact" value={form.contact_name || ""} onChange={(e) => set("contact_name", e.target.value)} />
      </div>
      <div>
        <Label>Téléphone</Label>
        <Input data-testid="cf-phone" value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} />
      </div>
      <div className="col-span-2">
        <Label>Adresse de livraison</Label>
        <Input data-testid="cf-address" value={form.address || ""} onChange={(e) => set("address", e.target.value)} placeholder="N°, rue, code postal" />
      </div>
      <div className="col-span-2">
        <Label>Ville</Label>
        <Input data-testid="cf-city" value={form.city || ""} onChange={(e) => set("city", e.target.value)} />
      </div>
      <div>
        <Label>Raison sociale (facture)</Label>
        <Input data-testid="cf-legal-name" value={form.legal_name || ""} onChange={(e) => set("legal_name", e.target.value)} placeholder="Ex : K & A ROUEN" />
      </div>
      <div>
        <Label>N° TVA / N.I.F. (facture)</Label>
        <Input data-testid="cf-vat" value={form.vat_number || ""} onChange={(e) => set("vat_number", e.target.value)} placeholder="Ex : FR37809134109" />
      </div>
      <div>
        <Label>SIRET</Label>
        <Input data-testid="cf-siret" value={form.siret || ""} onChange={(e) => set("siret", e.target.value)} placeholder="14 chiffres" />
      </div>
      <div>
        <Label>Type d'établissement</Label>
        <Input data-testid="cf-type" value={form.type_etablissement || ""} onChange={(e) => set("type_etablissement", e.target.value)} placeholder="Ex : Restaurant, Franchise" />
      </div>
    </div>
  );
}

function EditDialog({ client, onDone }) {
  const [form, setForm] = useState(client);
  const [open, setOpen] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    await api.put(`/clients/${client.id}`, { ...form, enseigne: client.enseigne });
    toast.success(`Dépôt « ${form.name} » mis à jour`);
    setOpen(false);
    onDone();
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setForm(client); }}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`edit-client-${client.id}`}>
          <Pencil className="mr-1 h-3.5 w-3.5" /> Modifier
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{client.name}</DialogTitle>
          <DialogDescription>Enseigne : {client.enseigne || "—"}. Renseignez l'email et l'adresse de livraison.</DialogDescription>
        </DialogHeader>
        <DepotFields form={form} set={set} />
        <DialogFooter>
          <Button onClick={save} data-testid="save-client-btn">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddDepotDialog({ enseigne, onDone }) {
  const [form, setForm] = useState({});
  const [open, setOpen] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.name || !form.email) { toast.error("Nom et email requis"); return; }
    await api.post("/clients", { ...form, enseigne });
    toast.success(`Dépôt ajouté à ${enseigne}`);
    setOpen(false);
    setForm({});
    onDone();
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setForm({}); }}>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost" data-testid={`add-depot-${enseigne}`}>
          <Plus className="mr-1 h-4 w-4" /> Ajouter un dépôt
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nouveau dépôt — {enseigne}</DialogTitle>
          <DialogDescription>Ce dépôt sera rattaché à l'enseigne {enseigne}.</DialogDescription>
        </DialogHeader>
        <DepotFields form={form} set={set} />
        <DialogFooter>
          <Button onClick={save} data-testid="save-depot-btn">Créer le dépôt</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddClientDialog({ onDone }) {
  const [enseigne, setEnseigne] = useState("");
  const [form, setForm] = useState({});
  const [open, setOpen] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!enseigne || !form.name || !form.email) { toast.error("Enseigne, nom du dépôt et email requis"); return; }
    if (!form.pin || form.pin.length < 6) { toast.error("PIN de connexion requis (min. 6 caractères)"); return; }
    await api.post("/clients", { ...form, enseigne });
    toast.success(`Client « ${enseigne} » créé`);
    setOpen(false);
    setEnseigne("");
    setForm({});
    onDone();
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) { setEnseigne(""); setForm({}); } }}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid="add-client-btn">
          <Plus className="mr-1 h-4 w-4" /> Ajouter un client
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nouveau client (enseigne)</DialogTitle>
          <DialogDescription>Créez une nouvelle enseigne indépendante avec son premier dépôt.</DialogDescription>
        </DialogHeader>
        <div className="mb-3">
          <Label>Nom de l'enseigne</Label>
          <Input data-testid="acf-enseigne" value={enseigne} onChange={(e) => setEnseigne(e.target.value)} placeholder="Nom de l'enseigne" />
        </div>
        <div className="mb-3">
          <Label>PIN de connexion de l'enseigne</Label>
          <Input data-testid="acf-pin" type="password" value={form.pin || ""} onChange={(e) => set("pin", e.target.value)} placeholder="Au moins 6 caractères" />
          <p className="mt-1 text-xs text-muted-foreground">Ce PIN permettra à l'enseigne de se connecter à son espace.</p>
        </div>
        <DepotFields form={form} set={set} />
        <DialogFooter>
          <Button onClick={save} data-testid="save-new-client-btn">Créer le client</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportDialog({ enseignes, onDone }) {
  const [open, setOpen] = useState(false);
  const [enseigne, setEnseigne] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const { public_catalog: catalogue } = useBrand();
  const save = async () => {
    if (!enseigne.trim() && !catalogue) { toast.error("Nom de l'enseigne requis"); return; }
    if (!file) { toast.error("Choisissez un fichier Excel (.xlsx)"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("enseigne", enseigne.trim());
      fd.append("file", file);
      const { data } = await api.post("/clients/import", fd);
      toast.success(`Import ${data.enseigne || "clients du catalogue"} : ${data.created} ajouté(s), ${data.updated} mis à jour`, {
        description: data.new_enseigne_pin ? `Nouvelle enseigne créée. PIN de connexion : ${data.new_enseigne_pin} (notez-le, il ne sera plus affiché).` : undefined,
        duration: data.new_enseigne_pin ? 12000 : 4000,
      });
      setOpen(false);
      setEnseigne("");
      setFile(null);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import impossible");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) { setEnseigne(""); setFile(null); } }}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid="import-clients-btn" className="gap-1.5">
          <Upload className="h-4 w-4" /> Importer Excel
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Importer des dépôts (Excel)</DialogTitle>
          <DialogDescription>
            Chargez un fichier .xlsx avec les colonnes : Dépôt, Adresse, Code Postal, Ville, Téléphone, Email (optionnel), Raison sociale (optionnel), SIRET (optionnel) et N° TVA (optionnel).
            Les dépôts existants (même nom) sont mis à jour, les nouveaux sont ajoutés.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Enseigne concernée{catalogue ? " (laisser vide pour des clients du catalogue, sans PIN)" : ""}</Label>
            <Input
              data-testid="import-enseigne-input"
              list="import-enseigne-list"
              value={enseigne}
              onChange={(e) => setEnseigne(e.target.value)}
              placeholder={catalogue ? "Vide = clients du catalogue" : "Nom de l'enseigne"}
            />
            <datalist id="import-enseigne-list">
              {(enseignes || []).map((n) => <option key={n} value={n} />)}
            </datalist>
          </div>
          <div>
            <Label>Fichier Excel (.xlsx)</Label>
            <Input
              data-testid="import-file-input"
              type="file"
              accept=".xlsx"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={busy} data-testid="import-submit-btn">{busy ? "Import…" : "Importer"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const ClientsManager = () => {
  const [clients, setClients] = useState([]);
  const { public_catalog: catalogue } = useBrand();
  const load = () => api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const groups = useMemo(() => {
    const map = {};
    clients.forEach((c) => {
      const e = c.enseigne || (catalogue ? "Clients du catalogue" : "Autres");
      (map[e] = map[e] || { list: [], none: !c.enseigne }).list.push(c);
    });
    return Object.entries(map).map(([e, g]) => [e, g.list, g.none]);
  }, [clients, catalogue]);

  return (
    <Card className="mt-6 overflow-hidden" data-testid="clients-manager">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-4">
        <div>
          <h3 className="font-display font-bold">Clients &amp; dépôts (indépendants)</h3>
          <p className="text-xs text-muted-foreground">Chaque enseigne est isolée. Renseignez l'email de confirmation et l'adresse de livraison de chaque dépôt.</p>
        </div>
        <div className="flex items-center gap-2">
          <ImportDialog enseignes={groups.filter((g) => !g[2]).map(([e]) => e)} onDone={load} />
          <AddClientDialog onDone={load} />
        </div>
      </div>

      <div className="divide-y divide-border">
        {groups.map(([enseigne, depots, sansEnseigne]) => (
          <div key={enseigne} data-testid={`enseigne-group-${enseigne}`}>
            <div className="flex items-center justify-between bg-muted/40 px-4 py-2.5">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-primary" />
                <span className="font-display text-sm font-bold">{enseigne}</span>
                <Badge variant="outline" className="text-xs">{depots.length} dépôt{depots.length > 1 ? "s" : ""}</Badge>
              </div>
              {!sansEnseigne && <AddDepotDialog enseigne={enseigne} onDone={load} />}
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Dépôt</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Téléphone</TableHead>
                  <TableHead>Code postal</TableHead>
                  <TableHead>Adresse de livraison</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {depots.map((c) => (
                  <TableRow key={c.id} data-testid={`client-manage-row-${c.id}`}>
                    <TableCell className="font-medium">{c.name}{c.siret && <span className="block text-xs font-normal text-muted-foreground">SIRET {c.siret}{c.type_etablissement ? ` · ${c.type_etablissement}` : ""}</span>}</TableCell>
                    <TableCell>
                      <span className="flex flex-wrap items-center gap-1.5 text-sm">
                        <Mail className="h-3.5 w-3.5 text-muted-foreground" /> {c.email}
                        {isPlaceholder(c.email) ? (
                          <Badge variant="outline" className="border-secondary/40 bg-secondary/10 text-secondary">
                            <AlertCircle className="mr-1 h-3 w-3" /> à confirmer
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="border-primary/40 bg-primary/10 text-primary">
                            <CheckCircle2 className="mr-1 h-3 w-3" /> ok
                          </Badge>
                        )}
                      </span>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-muted-foreground" data-testid={`client-phone-${c.id}`}>{c.phone ? c.phone : "—"}</TableCell>
                    <TableCell className="whitespace-nowrap text-sm tabular text-muted-foreground" data-testid={`client-cp-${c.id}`}>{postalCode(c) || "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{c.address ? `${c.address}${c.city ? ", " + c.city : ""}` : (c.city || "—")}</TableCell>
                    <TableCell className="text-right"><EditDialog client={c} onDone={load} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ))}
      </div>
    </Card>
  );
};
